import { useState } from "react";
import { toast } from "sonner";
import { Upload, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const TRADE_SAMPLE = `user_email,instrument,side,lots,entry_price,stop_loss,take_profit
client@xmforex.com,EUR/USD,buy,0.10,1.08500,1.08000,1.09500
client@xmforex.com,GBP/USD,sell,0.05,1.27300,1.28000,1.26000`;

const METHODS_SAMPLE = `user_email,btc_address,usdt_address,usdt_network,bank_name,bank_ifsc,bank_account_name,bank_account_number,bank_swift
client@xmforex.com,bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh,TXYZabc123DefGh456IjKlmN789OpQrSt,TRC20,HDFC Bank,HDFC0001234,John Doe,50100123456789,HDFCINBB`;

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];
  const headers = lines[0].split(",").map((h) => h.trim());
  return lines.slice(1).filter(Boolean).map((line) => {
    const cells = line.split(",").map((c) => c.trim());
    const row = {};
    headers.forEach((h, i) => { row[h] = cells[i] ?? ""; });
    return row;
  });
}

export default function BulkUpload() {
  return (
    <div className="max-w-4xl" data-testid="bulk-upload-page">
      <Tabs defaultValue="trades">
        <TabsList className="bg-card border border-subtle">
          <TabsTrigger value="trades" data-testid="bulk-tab-trades">Bulk Trades</TabsTrigger>
          <TabsTrigger value="methods" data-testid="bulk-tab-methods">Deposit Methods</TabsTrigger>
        </TabsList>
        <TabsContent value="trades" className="mt-4">
          <TradesBulk />
        </TabsContent>
        <TabsContent value="methods" className="mt-4">
          <MethodsBulk />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function TradesBulk() {
  const [csv, setCsv] = useState(TRADE_SAMPLE);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result));
    reader.readAsText(f);
  };

  const submit = async () => {
    const rows = parseCSV(csv).map((r) => ({
      ...r,
      lots: parseFloat(r.lots),
      entry_price: parseFloat(r.entry_price),
      stop_loss: r.stop_loss ? parseFloat(r.stop_loss) : null,
      take_profit: r.take_profit ? parseFloat(r.take_profit) : null,
    }));
    if (rows.length === 0) return toast.error("No valid rows found");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/trades/bulk", rows);
      setResult(data);
      toast.success(`Created ${data.created} trades${data.errors.length ? `, ${data.errors.length} errors` : ""}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section
      title="Bulk Open Trades"
      helper={<>Required columns: <span className="font-mono-num text-foreground">user_email, instrument, side, lots, entry_price</span>. Optional: <span className="font-mono-num text-foreground">stop_loss, take_profit</span>.</>}
      csv={csv}
      setCsv={setCsv}
      onFile={onFile}
      submit={submit}
      busy={busy}
      testId="submit-bulk-trades"
      result={result}
    />
  );
}

function MethodsBulk() {
  const [csv, setCsv] = useState(METHODS_SAMPLE);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result));
    reader.readAsText(f);
  };

  const submit = async () => {
    const rows = parseCSV(csv);
    if (rows.length === 0) return toast.error("No valid rows found");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/deposit-methods/bulk", rows);
      setResult(data);
      toast.success(`Updated ${data.updated} clients${data.errors.length ? `, ${data.errors.length} errors` : ""}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section
      title="Bulk Deposit Methods"
      helper={<>Columns (any subset): <span className="font-mono-num text-foreground">user_email, btc_address, usdt_address, usdt_network, bank_name, bank_ifsc, bank_account_name, bank_account_number, bank_swift</span>. Only non-empty cells overwrite existing values.</>}
      csv={csv}
      setCsv={setCsv}
      onFile={onFile}
      submit={submit}
      busy={busy}
      testId="submit-bulk-methods"
      result={result}
      resultKey="updated"
    />
  );
}

function Section({ title, helper, csv, setCsv, onFile, submit, busy, testId, result, resultKey = "created" }) {
  return (
    <div className="space-y-6">
      <div className="border border-subtle bg-card p-8 space-y-6">
        <div>
          <p className="label-eyebrow mb-2">CSV Format</p>
          <h3 className="text-lg font-semibold mb-2">{title}</h3>
          <p className="text-sm text-muted-foreground">{helper}</p>
        </div>
        <label className="border border-dashed border-subtle rounded p-6 flex items-center justify-center gap-3 cursor-pointer hover:bg-surface-hover transition-colors">
          <Upload className="w-5 h-5 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Upload a .csv file or paste below</span>
          <input type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" />
        </label>
        <div>
          <div className="label-eyebrow mb-2 flex items-center gap-2"><FileText className="w-3 h-3" /> CSV content</div>
          <Textarea value={csv} onChange={(e) => setCsv(e.target.value)} rows={10} className="font-mono-num text-xs bg-background border-subtle" />
        </div>
        <Button onClick={submit} disabled={busy} className="w-full h-11" data-testid={testId}>
          {busy ? "Uploading…" : "Submit"}
        </Button>
      </div>

      {result && (
        <div className="border border-subtle bg-card p-6">
          <p className="label-eyebrow mb-2">Result</p>
          <div className="font-mono-num">
            <div className="text-profit">{resultKey === "updated" ? "Updated" : "Created"}: {result[resultKey]}</div>
            {result.errors?.length > 0 && (
              <div className="text-loss mt-2 space-y-1 text-sm">
                {result.errors.map((e, i) => (<div key={i}>Row {e.row}: {e.error}</div>))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
